import { useState } from 'react'
import api from '../services/api'
import lodashSet from 'lodash.set'
import lodashGet from 'lodash.get'
import lodashIsEmpty from 'lodash.isempty'

export const ACCOUNT_FEATURES = {
    Tempo: 'tempo',
    RealTimeDashboard: 'realTimeDashboard',
    Screens: 'screens',
    Insights: 'insights',
    HideStores: "hideStores",
    AutomaticDestinationCreation: 'automaticDestinationCreation',
    NowReadyConfiguration: 'nowReadyConfiguration',
    HelloSso: 'helloSso',
    CanvasSso: 'canvasSso',
    HelloStateTemplates: 'helloStateTemplates',
    HelloStack: 'helloStack',
    PushNotifications: 'pushNotifications'
}

const GENERIC_ERROR_MESSAGE = 'An error occurred, check the browser console for details.'

export function useAccountFeatures({ accountId }) {
    const accessToken = localStorage.getItem('idToken')
    const [loading, setLoading] = useState()
    const [features, setAccountFeatures] = useState({})
    const [updateErrors, setUpdateErrors] = useState({})
    const [retrievalError, setRetrievalError] = useState({})

    // Error is nullable here in order to clear errors when required
    const setErrors = (path, error) =>
        setUpdateErrors((currentErrors) => {
            const currentErrorsCopy = JSON.parse(JSON.stringify(currentErrors))
            if (error) {
                const errorMessage = error?.response?.data?.errorMessages?.errorDescription
                if(errorMessage){
                    return lodashSet(currentErrorsCopy, path, { error, msg: errorMessage })
                }
                return lodashSet(currentErrorsCopy, path, { error, msg: GENERIC_ERROR_MESSAGE })
            } else {
                return lodashSet(currentErrorsCopy, path, undefined)
            }
        })

    /**
     * Converts
     * currentFeatures={}, path='tempo.enabled', value='newValue'
     * to
     * { path: '/features/tempo', value: {enabled: 'newValue'}, op: 'add' }
     */
    const convertFeatureUpdatesToPatchOperations = (path, value, op) => {
        let pPath
        let pValue
        const pathParts = path.split('.')
        if (lodashIsEmpty(features)) {
            pPath = '/features'
            pValue = lodashSet({}, path, value)
        } else if (pathParts.length === 1) {
            pPath = `/features/${pathParts[0]}`
            pValue = value
        } else {
            pPath = `/features/${pathParts[0]}`
            pValue = lodashSet(features[pathParts[0]] || {}, pathParts.slice(1).join('.'), value)
        }
        const operation = { op: op || 'add', path: pPath };

        if (op !== "remove")  {
            operation["value"] = pValue
        }
        return [
            operation
        ]
    }

    return {
        loading,
        features,
        updateErrors,
        retrievalError,

        retrieveFeatures:
            async () => {
                try {
                    setLoading(true)

                    const accFeatures = await api.getAccountFeatures({ accountId, accessToken })

                    setAccountFeatures(accFeatures)
                } catch(err) {
                    setRetrievalError(err)
                }
                setLoading(false)
            },

        /**
         * Example Usage:
         * patchFeatures({ '/tempo/enabled': 'newValue' })
         */
        patchFeatures:
            async (path, value, op) => {
                try{
                    const patches = convertFeatureUpdatesToPatchOperations(path, value, op)

                    setErrors(path, undefined)

                    setLoading(true)

                    const accFeatures = await api.patchAccountFeatures({ accountId, accessToken, patches })

                    setAccountFeatures(accFeatures)
                    return { success: true }
                } catch (err) {
                    console.log('account-features - patchFeatures error:', err)
                    setErrors(path, err)
                    return { success: false }
                } finally {
                    setLoading(false)
                }
            }
    }
}
